import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Badge, FilterButton, Pagination, Spinner } from '@/components/ui'
import { useUrlFilters } from '@/hooks/useUrlFilters'

import { fetchBiens, setFilters, bienFilters, ordonnedBienFilters } from './biensSlice'
import BienItem from './BienItem'
import AddBienButton from './AddBienButton'

const BiensList = ({archived}) => {
  const dispatch = useDispatch()
  const biens = useSelector((state) => state.biens.items)
  const filters = useSelector((state) => state.biens.filters)
  const pagination = useSelector((state) => state.biens.pagination)
  const isLoading = useSelector((state) => state.biens.loadingStatus.fetchList)

  useEffect(() => {
    if (archived) {
      dispatch(setFilters({archived}))
    } else {
      dispatch(setFilters())
    }
  }, []) // Exécuté une seule fois au montage

  useUrlFilters({
    setFiltersAction: setFilters,
    fetchAction: fetchBiens,
    filters,
    allowedParams: ['type', 'mandat', 'page', 'archived', 'limit'],
    waitForDefaultFilters: true
  })

  const handlePageChange = (page) => {
    dispatch(setFilters({ page }))
  } 

  const handleGetList = (event) => {
    event.preventDefault()
    // Inverser le filtre "archivés"
    dispatch(setFilters({archived: !filters.archived}))
  }
  
  const handleFiltersChange = (newFilters) => {
    const filterUpdates = {
      // Réinitialiser la pagination
      page: 1
    }
  
    // Vérifier si le filtre 'type' est présent dans newFilters
    if ('type' in newFilters) {
      // Si oui, utiliser la nouvelle valeur
      filterUpdates.type = newFilters.type
    } else {
      // Si non, réinitialiser ce filtre
      filterUpdates.type = undefined
    }
    
    // Vérifier si le filtre 'nature' est présent dans newFilters
    if ('mandat' in newFilters) {
      // Si oui, utiliser la nouvelle valeur
      filterUpdates.mandat = newFilters.mandat
    } else {
      // Si non, réinitialiser ce filtre
      filterUpdates.mandat = undefined
    }
    
    // Mettre à jour les filtres dans le state Redux
    dispatch(setFilters(filterUpdates))
  }

  const getFilterBadgeText = (filterKey) => {
    const value = filters[filterKey]
    if(!value) return null;
    return bienFilters[value] ? bienFilters[value].label.toLowerCase() : null
  }

  return (
    <div>
      <div className='flex items-center justify-between'>
        <div className="mb-4">
          <h1 className="text-xl font-semibold mb-4">
            {filters.archived ? 'Les Biens archivés' : 'Les Biens'}
          </h1>
          <div>
            { ['type', 'mandat'].map( (filterKey, index) => (
              getFilterBadgeText(filterKey) && 
                <Badge key={index} text={getFilterBadgeText(filterKey)} className='mr-2'/>
            ))}
          </div>

        </div>

        <div className="flex items-right justify-between items-center mb-4">
          {filters.archived ?
            <div className="hover:underline font-bold mr-4">
              <a href="/biens" onClick={handleGetList} className="text-sm text-gray-600 hover:text-gray-800">
                Retour aux biens actifs
              </a>
            </div> :
            <div className="hover:underline font-bold mr-4">
              <a href="/biens?archived=true" onClick={handleGetList} className="text-sm text-gray-600 hover:text-gray-800">
                Voir les biens archivés
              </a>
            </div>
          }
          <div className="dropdown">
            <FilterButton 
              filters={ordonnedBienFilters(filters)} 
              onChange={handleFiltersChange}
            />
          </div>
          
          <div>
            <AddBienButton disabled={filters.archived || false} />
          </div>
        </div>
      </div>

      <div className="list-container">
        <table className="table table-zebra w-full">
          <thead>
            <tr>
              <th>Codage</th>
              <th>Statut</th>
              <th>Mandat</th>
              <th>Prix</th>
              <th>Adresse</th>
              <th>Type</th>
              <th>Surface</th>
              <th>Propriétaire</th>
              <th>Locataire</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            { isLoading && <tr>
                <td colSpan="10" className="text-center py-4 text-gray-600 italic">
                  <Spinner color='primary'/>
                </td>
              </tr>
            }
            {!isLoading && biens.length === 0 && 
              <tr>
                <td colSpan="10" className="text-center py-4 text-gray-600 italic">
                  { isLoading ? <Spinner color='primary'/> : "Aucun bien trouvé" }
                </td>
              </tr>
            }
            { !isLoading && biens.length > 0 && 
              biens.filter(item => filters.archived ? !!item.archived_at : !item.archived_at)
                .map((bien) => (
                <BienItem key={bien.id} bien={bien} />
              ))
            }
          </tbody>
        </table>
      </div>
      {pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
        />
      )} 
    </div>
  )
}

export default BiensList